import { component, prop } from '../../../decorators';

import InputMask from 'inputmask-core';
import ffpoly from './ff-polyfill.js'; //Firefox Polyfill for focus events
ffpoly();

import isString from 'lodash/isString';
import has from 'lodash/has';
import Logger from '../../../logger';

const logger = new Logger('MaskedInput');

const KEYS = {
   BACKSPACE   : 8,
   LEFT        : 37,
   RIGHT       : 39,
   END         : 35,
   HOME        : 36,
   DELETE      : 46
};

const KEY_TO_ACTION = {
   [KEYS.BACKSPACE]: 'onBackspacePressed',
   [KEYS.LEFT]:      'onLeftPressed',
   [KEYS.RIGHT]:     'onRightPressed',
   [KEYS.END]:       'onEndPressed',
   [KEYS.HOME]:      'onHomePressed',
   [KEYS.DELETE]:    'onDeletePressed'
};

const notEmpty = s => isString(s) && !!s;
const isChar = s => isString(s) && s.length === 1; // eslint-disable-line no-magic-numbers

const FORMATS = {
   a: {
      validate: char => /^[A-Za-z]$/.test(char)
   },
   A: {
      validate: char => /^[A-Za-z]$/.test(char),
      transform: char => char.toUpperCase()
   },
   '*': {
      validate: char => /^[\dA-Za-z]$/.test(char)
   },
   '#': {
      validate: char => /^[\dA-Za-z]$/.test(char),
      transform: char => char.toUpperCase()
   },
   '+': {
      validate: () => true
   }
};

@component({
   watch: {
      mask: 'initMask',
      value: 'changeValue'
   }
})
export default class MaskedInput {
   marginLeft = 0  // eslint-disable-line no-magic-numbers
   core = null
   updateAfterAll = false

   @prop({ type: String }) value
   @prop({ type: String, required: true, validator: notEmpty }) mask
   @prop({ type: String, default: '_', validator: isChar }) placeholderChar
   @prop({ type: Boolean, default: false }) disabled
   @prop({ type: Boolean, default: false }) readonly

   changeValue(val) {
      if (this.core) {
         this.core.setValue(val); //For multiple inputs support
      }
   }

   initMask() {
      try {
         this.core = new InputMask({
            pattern: this.mask,
            value: '',
            placeholderChar: this.placeholderChar,
            formatCharacters: FORMATS
         });
         for (let i = 0; i < this.$refs.input.value.length; ++i) {
            this.core.input(this.$refs.input.value[i]);
         }
         this.core.setSelection({ start: 0, end: 0 });
         if (this.$refs.input.value === '') {
            this.$emit('input', '', '');
         } else {
            this.updateToCoreState();
         }
      } catch (e) {
         logger.error(e.message);
         this.core = null;
         this.$emit('input', this.$refs.input.value, '');
      }
   }

   getValue() {
      if (this.core === null) {
         return '';
      }
      return this.core.getValue();
   }

   onBackspacePressed() {
      const { start, end } = this.core.selection;
      if (start > this.marginLeft || start !== end) {
         this.core.backspace();
         this.updateToCoreState();
      }
   }

   onLeftPressed() {
      if (this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
         this.$refs.input.selectionEnd = this.$refs.input.selectionStart--;
      }

      this.core.selection = {
         start: this.$refs.input.selectionStart,
         end: this.$refs.input.selectionStart
      };
      this.updateToCoreState();
   }

   onRightPressed() {
      if (this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
         this.$refs.input.selectionEnd++;
      }

      this.core.selection = {
         start: this.$refs.input.selectionEnd,
         end: this.$refs.input.selectionEnd
      };
      this.updateToCoreState();
   }

   onEndPressed() {
      this.$refs.input.selectionStart = this.$refs.input.selectionEnd = this.$refs.input.value.length;
      this.core.selection = {
         start: this.$refs.input.selectionEnd,
         end: this.$refs.input.selectionEnd
      };
      this.updateToCoreState();
   }

   onHomePressed() {
      this.$refs.input.selectionStart = this.$refs.input.selectionEnd = 0;
      this.core.selection = {
         start: this.$refs.input.selectionStart,
         end: this.$refs.input.selectionStart
      };
      this.updateToCoreState();
   }

   onDeletePressed() {
      if (this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
         this.core.setValue('');
         this.core.setSelection({ start: 0, end: 0 });
         this.$refs.input.selectionStart = this.core.selection.start;
         this.$refs.input.selectionEnd = this.core.selection.start;

      } else {
         this.core.backspace();
      }
      this.updateToCoreState();
   }

   keyDown(e) {
      if (this.core === null) {
         e.preventDefault();
         return;
      }
      this.setNativeSelection();

      const action = KEY_TO_ACTION[e.keyCode];
      if ( action && has(this, action) ) {
         e.preventDefault();
         this[action]();
      }
   }

   input(/*e*/) {
      // noop
   }

   keyPress(e) {
      if (e.ctrlKey) {
         return; //Fix FF copy/paste issue
      }
      /*
         IE & FF are not trigger textInput event, so we have to force it
      */
      //by http://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
      let isIE = /*@cc_on!@*/false || !!document.documentMode;
      let isFirefox = typeof InstallTrigger !== 'undefined';

      if (isIE || isFirefox) {
         e.preventDefault();
         e.data = e.key;
         this.textInput(e);
      }
   }

   textInput(e) {
      if (e.preventDefault) {
         e.preventDefault();
      }
      if (this.core.input(e.data)) {
         this.updateAfterAll = true;
      }
      this.updateToCoreState();
   }

   keyUp(/*e*/) {
      this.updateToCoreState();
      this.updateAfterAll = false;
   }

   cut(e) {
      e.preventDefault();
      if (this.$refs.input.selectionStart !== this.$refs.input.selectionEnd) {
         /*let text = this.$refs.input.value.slice(
            this.$refs.input.selectionStart,
            this.$refs.input.selectionEnd
         )*/
         try {
            document.execCommand('copy');
         } catch (err) {
            logger.error(err);
         }
         this.core.backspace();
         this.updateToCoreState();
      }
   }

   copy(/*e*/) {
      // noop
   }

   paste(e) {
      e.preventDefault();
      let text = e.clipboardData.getData('text');
      for (let i = 0; i < text.length; ++i) {
         this.core.input(text[i]);
      }
      this.updateToCoreState();
   }

   updateToCoreState() {
      if (this.core === null) {
         return;
      }
      if (this.$refs.input.value !== this.core.getValue()) {
         this.$refs.input.value = this.core.getValue();
         this.$emit('input', this.$refs.input.value, this.core.getRawValue());
      }
      this.$refs.input.selectionStart = this.core.selection.start;
      this.$refs.input.selectionEnd = this.core.selection.end;
   }

   focusin(/*e*/) {
      // noop
   }

   isEmpty() {
      if (this.core === null) {
         return true;
      }
      return this.core.getValue() === this.core.emptyValue;
   }

   focusout(/*e*/) {
      if (this.isEmpty()) {
         this.$refs.input.value = '';
         this.core.setSelection({ start: 0, end: 0 });
         this.$emit('input', '', '');
      }
   }

   setNativeSelection() {
      this.core.selection = {
         start: this.$refs.input.selectionStart,
         end: this.$refs.input.selectionEnd
      };
   }

   mouseUp(/*e*/) {
      if (this.isEmpty() && this.$refs.input.selectionStart === this.$refs.input.selectionEnd) {
         this.core.setSelection({ start: 0, end: 0 });
         this.$refs.input.selectionStart = this.core.selection.start;
         this.$refs.input.selectionEnd = this.core.selection.start;
         this.marginLeft = this.core.selection.start;
         this.updateToCoreState();
      } else {
         this.setNativeSelection();
      }
   }

   mounted() {
      this.initMask();
   }
}
